import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
    FaGoogle,
    FaInstagram,
    FaPeopleArrows,
    FaWhatsapp,
    FaYoutube,
} from "react-icons/fa";
import { fightersCollection, getFighters } from "../utils/firebase";
import {
    Angry,
    ChevronDown,
    ChevronLeft,
    ChevronRight,
    ChevronUp,
    Image,
    ImageOff,
    Mail,
    Mars,
    Phone,
    Venus,
    X,
} from "lucide-react";
import toast from "react-hot-toast";
import { formatFirestoreTimestamp } from "../utils/common";
import ManageFighter from "./manage-fighter";
import {
    ADMIN_TABLE_FETCH_SIZE,
    fighterEnties,
    fighterEntryDisplayMapping,
    fighterEntryTooltipMapping,
    weightClasses,
} from "../constants";
import useDebounce from "../hooks/use-debounce";
import {
    getDocs,
    limit,
    orderBy,
    query,
    startAfter,
    where,
    doc,
    getDoc,
} from "firebase/firestore";

export default function ManageFighters() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [fighters, setFighters] = useState([]);
    const [lastDoc, setLastDoc] = useState(null);
    const [sort, setSort] = useState("name");
    const [order, setOrder] = useState("asc");
    const [hasMore, setHasMore] = useState(true);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const size = ADMIN_TABLE_FETCH_SIZE;

    const debouncedSearchTerm = useDebounce(searchTerm, 1000);

    const [selectedFighter, setSelectedFighter] = useState(null);

    // A ref for the IntersectionObserver's sentinel element
    const observerRef = useRef(null);

    // Use a ref for the loading flag to avoid triggering re-creations of fetchData
    const loadingRef = useRef(false);

    // Track the last fighter ID we processed from URL to avoid unnecessary updates
    const lastProcessedFighterIdRef = useRef(null);

    const fetchFighterById = useCallback(async (fighterId) => {
        try {
            const fighterDoc = await getDoc(doc(fightersCollection, fighterId));

            if (!fighterDoc.exists()) {
                toast.error("Fighter not found");
                return null;
            }

            const data = fighterDoc.data();

            // Filter out deleted fighters
            if (data.deletedAt) {
                toast.error("Fighter has been deleted");
                return null;
            }

            // Calculate totals using same logic as pagination
            let totalWin = fighterEnties
                .map((entry) => {
                    return data?.records?.[entry]?.win ?? 0;
                })
                .reduce((a, b) => {
                    return a + (parseInt(b) || 0);
                }, 0);
            let totalLoss = fighterEnties
                .map((entry) => {
                    return data?.records?.[entry]?.loss ?? 0;
                })
                .reduce((a, b) => {
                    return a + (parseInt(b) || 0);
                }, 0);
            let totalDraw = fighterEnties
                .map((entry) => {
                    return data?.records?.[entry]?.draw ?? 0;
                })
                .reduce((a, b) => {
                    return a + (parseInt(b) || 0);
                }, 0);

            return {
                id: fighterDoc.id,
                ...data,
                totalWin: totalWin,
                totalLoss: totalLoss,
                totalDraw: totalDraw,
            };
        } catch (error) {
            console.error("Error fetching fighter by ID:", error);
            toast.error("Failed to load fighter");
            return null;
        }
    }, []);

    const handleRowClick = (fighter) => {
        setSelectedFighter(fighter);
        setSearchParams({ fighter: fighter.id });
    };

    const handleRemoveSelectedFighter = () => {
        setSelectedFighter(null);
        setSearchParams({});
    };

    const handleOnPanelClose = (
        updatedFighterInfo,
        newProfileImageURL,
        isDeleted
    ) => {
        handleRemoveSelectedFighter();
        if (isDeleted && selectedFighter) {
            // Remove deleted fighter from the list
            setFighters(
                (prev) => prev?.filter((f) => f.id !== selectedFighter.id) ?? []
            );
        } else if (!!newProfileImageURL && !updatedFighterInfo) {
            setFighters(
                (prev) =>
                    prev?.map((f) =>
                        f.id === selectedFighter.id
                            ? { ...f, fighterPicture: newProfileImageURL }
                            : f
                    ) ?? []
            );
        } else if (!!updatedFighterInfo) {
            setFighters((prev) => {
                if (!updatedFighterInfo.id) {
                    //new fighter register
                    return [updatedFighterInfo, ...prev];
                } else {
                    //update fighter info
                    return (
                        prev?.map((f) =>
                            f.id === updatedFighterInfo.id
                                ? { ...f, ...updatedFighterInfo }
                                : f
                        ) ?? []
                    );
                }
            });
        }
        handleRemoveSelectedFighter();
    };

    const handleTableHeaderClick = (field) => {
        if (
            ["fighterPicture", "totalWin", "totalLoss", "totalDraw"].includes(
                field
            )
        ) {
            return;
        }
        if (field === sort) {
            setOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
        } else {
            setSort(field);
            setOrder("asc");
        }
    };

    const buildQuery = useCallback(
        (reset = false) => {
            const constraints = [];

            if (debouncedSearchTerm.trim() !== "") {
                constraints.push(where("name", ">=", debouncedSearchTerm));
                constraints.push(
                    where("name", "<=", debouncedSearchTerm + "\uf8ff")
                );

                constraints.push(orderBy("name", "asc"));
            } else {
                constraints.push(orderBy(sort, order));
            }

            constraints.push(limit(size));

            if (!reset && !!lastDoc) {
                constraints.push(startAfter(lastDoc));
            }

            return query(fightersCollection, ...constraints);
        },
        [debouncedSearchTerm, lastDoc, order, size, sort]
    );

    const fetchData = useCallback(
        async (reset = false) => {
            if (loadingRef.current) return;
            loadingRef.current = true;
            setLoading(true);

            try {
                const q = buildQuery(reset);
                const snapshot = await getDocs(q);
                const fetchedFighters =
                    snapshot?.docs
                        ?.map((doc) => {
                            const data = doc.data();

                            // Filter out deleted fighters (client-side filtering since deletedAt is a new field)
                            if (data.deletedAt) {
                                return null;
                            }

                            let totalWin = fighterEnties
                                .map((entry) => {
                                    return data?.records?.[entry]?.win ?? 0;
                                })
                                .reduce((a, b) => {
                                    return a + (parseInt(b) || 0);
                                }, 0);
                            let totalLoss = fighterEnties
                                .map((entry) => {
                                    return data?.records?.[entry]?.loss ?? 0;
                                })
                                .reduce((a, b) => {
                                    return a + (parseInt(b) || 0);
                                }, 0);
                            let totalDraw = fighterEnties
                                .map((entry) => {
                                    return data?.records?.[entry]?.draw ?? 0;
                                })
                                .reduce((a, b) => {
                                    return a + (parseInt(b) || 0);
                                }, 0);

                            return {
                                id: doc.id,
                                ...data,
                                totalWin: totalWin,
                                totalLoss: totalLoss,
                                totalDraw: totalDraw,
                            };
                        })
                        ?.filter((fighter) => fighter !== null) ?? [];

                setFighters((prev) => {
                    if (reset) {
                        return fetchedFighters;
                    } else {
                        const arr = [...prev, ...fetchedFighters];
                        const seenIds = new Set();
                        const uniqueData = arr.filter((item) => {
                            if (seenIds.has(item.id)) {
                                return false;
                            }
                            seenIds.add(item.id);
                            return true;
                        });
                        return uniqueData;
                    }
                });

                const lastVisible =
                    snapshot.docs[snapshot.docs.length - 1] || null;
                setLastDoc(lastVisible);

                setHasMore(snapshot.docs.length === size);
            } catch (error) {
                console.error("Error fetching fighters data: ", error);
            }
            loadingRef.current = false;
            setLoading(false);
        },
        [buildQuery, size]
    );

    useEffect(() => {
        setFighters([]);
        setLastDoc(null);
        setHasMore(true);
        fetchData(true);
    }, [sort, order, debouncedSearchTerm]);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            const firstEntry = entries[0];
            if (firstEntry.isIntersecting && hasMore && !loadingRef.current) {
                fetchData();
            }
        });

        const currentObserverRef = observerRef.current;
        if (currentObserverRef) {
            observer.observe(currentObserverRef);
        }
        return () => {
            if (currentObserverRef) {
                observer.unobserve(currentObserverRef);
            }
        };
    }, [hasMore, fetchData]);

    useEffect(() => {
        const fighterId = searchParams.get("fighter");

        if (fighterId) {
            // Check if fighter is already in the list (might have loaded via pagination)
            const existingFighter = fighters.find((f) => f.id === fighterId);

            if (existingFighter) {
                // Only update if not already selected to avoid unnecessary re-renders
                setSelectedFighter((prev) => {
                    if (prev?.id === fighterId) {
                        return prev;
                    }
                    lastProcessedFighterIdRef.current = fighterId;
                    return existingFighter;
                });
            } else if (lastProcessedFighterIdRef.current !== fighterId) {
                // Only fetch if we haven't processed this ID yet
                lastProcessedFighterIdRef.current = fighterId;
                fetchFighterById(fighterId).then((fighter) => {
                    if (fighter) {
                        setSelectedFighter(fighter);
                    } else {
                        // If fetch failed, reset ref so we can retry if needed
                        lastProcessedFighterIdRef.current = null;
                    }
                });
            }
        } else {
            // If URL param is cleared, clear selection and reset ref
            lastProcessedFighterIdRef.current = null;
            setSelectedFighter((prev) => (prev ? null : prev));
        }
    }, [searchParams, fighters, fetchFighterById]);

    const headers = {
        fighterPicture: "Profile Picture",
        name: "Name",
        totalWin: "Win",
        totalLoss: "Loss",
        totalDraw: "Draw",
        age: "Age",
        gender: "Gender",
        weightClass: "Weight",
        height: "Height(cm)",
        nationality: "Nationality",
        birthdate: "Birthdate",
        icOrPassport: "IC / Passport",
        gymNameOrTeamName: "Gym Name / TeamName",
        walkOutMusic: "Music",
        email: "Email",
        igUsername: "IG",
        phoneNumber: "Phone",
        emergencyContactNumber: "Emergency Contact",
    };

    return (
        <div className="flex flex-row w-full h-full">
            <div className="flex-center flex-1 items-start w-full overflow-auto h-full">
                <div className="overflow-auto w-full h-full">
                    <table className="table w-auto  ">
                        {/* head */}
                        <thead>
                            <tr className="[&>th]:text-center">
                                <th></th>
                                {Object.keys(headers).map((key, index) => {
                                    const header = headers[key];
                                    return (
                                        <th
                                            key={key}
                                            className="hover:bg-base-200 cursor-pointer"
                                            onClick={() =>
                                                handleTableHeaderClick(key)
                                            }
                                        >
                                            <div className="flex flex-row justify-center items-center gap-2">
                                                {key === "fighterPicture" ? (
                                                    <Image />
                                                ) : (
                                                    <p>{header}</p>
                                                )}
                                                {sort === key ? (
                                                    order === "desc" ? (
                                                        <ChevronUp />
                                                    ) : (
                                                        <ChevronDown />
                                                    )
                                                ) : null}
                                            </div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {fighters.map((fighter, index) => {
                                return (
                                    <tr
                                        key={index}
                                        className={
                                            "hover [&>td]:text-nowrap cursor-pointer " +
                                            (fighter.id === selectedFighter?.id
                                                ? "!bg-base-300"
                                                : "")
                                        }
                                        onClick={() => handleRowClick(fighter)}
                                    >
                                        <th>{index + 1}</th>
                                        <td>
                                            <img
                                                className="object-center aspect-square object-cover w-12 max-w-12"
                                                src={fighter.fighterPicture}
                                                alt={`${fighter.name}`}
                                            />
                                        </td>
                                        <td>{fighter.name}</td>
                                        {/* <td>{fighterEntryDisplayMapping[fighter.entry] ?? "?"}</td> */}
                                        <td className="text-center text-green-400">
                                            <div
                                                className="flex-center tooltip"
                                                data-tip={fighterEnties
                                                    .map((entry) => {
                                                        return `${fighterEntryTooltipMapping[
                                                            entry
                                                            ]
                                                            }: ${fighter?.records?.[
                                                                entry
                                                            ]?.win ?? 0
                                                            }`;
                                                    })
                                                    .join(", ")}
                                            >
                                                {fighterEnties
                                                    .map((entry) => {
                                                        return (
                                                            fighter?.records?.[
                                                                entry
                                                            ]?.win ?? 0
                                                        );
                                                    })
                                                    .reduce((a, b) => {
                                                        return (
                                                            a +
                                                            (parseInt(b) || 0)
                                                        );
                                                    }, 0)}
                                            </div>
                                        </td>
                                        <td className="text-center text-red-400">
                                            <div
                                                className="flex-center tooltip"
                                                data-tip={fighterEnties
                                                    .map((entry) => {
                                                        return `${fighterEntryTooltipMapping[
                                                            entry
                                                            ]
                                                            }: ${fighter?.records?.[
                                                                entry
                                                            ]?.loss ?? 0
                                                            }`;
                                                    })
                                                    .join(", ")}
                                            >
                                                {fighterEnties
                                                    .map((entry) => {
                                                        return (
                                                            fighter?.records?.[
                                                                entry
                                                            ]?.loss ?? 0
                                                        );
                                                    })
                                                    .reduce((a, b) => {
                                                        return (
                                                            a +
                                                            (parseInt(b) || 0)
                                                        );
                                                    }, 0)}
                                            </div>
                                        </td>
                                        <td className="text-center text-blue-400">
                                            <div
                                                className="flex-center tooltip"
                                                data-tip={fighterEnties
                                                    .map((entry) => {
                                                        return `${fighterEntryTooltipMapping[
                                                            entry
                                                            ]
                                                            }: ${fighter?.records?.[
                                                                entry
                                                            ]?.draw ?? 0
                                                            }`;
                                                    })
                                                    .join(", ")}
                                            >
                                                {fighterEnties
                                                    .map((entry) => {
                                                        return (
                                                            fighter?.records?.[
                                                                entry
                                                            ]?.draw ?? 0
                                                        );
                                                    })
                                                    .reduce((a, b) => {
                                                        return (
                                                            a +
                                                            (parseInt(b) || 0)
                                                        );
                                                    }, 0)}
                                            </div>
                                        </td>
                                        <td className="text-end">
                                            {fighter.age}
                                        </td>
                                        <td>
                                            <div className="flex-center">
                                                {fighter?.gender?.toUpperCase() ===
                                                    "M" ? (
                                                    <Mars className="text-blue-300" />
                                                ) : fighter?.gender?.toUpperCase() ===
                                                    "F" ? (
                                                    <Venus className="text-pink-300" />
                                                ) : (
                                                    "?"
                                                )}
                                            </div>
                                        </td>
                                        <td className="text-center">
                                            <div
                                                className="flex-center tooltip"
                                                data-tip={
                                                    weightClasses[
                                                    fighter.weightClass?.toUpperCase()
                                                    ] ?? "???"
                                                }
                                            >
                                                {fighter.weightClass?.toUpperCase() ??
                                                    "?"}
                                            </div>
                                        </td>
                                        <td className="text-end">
                                            {fighter.height}
                                        </td>
                                        <td>{fighter.nationality}</td>
                                        <td>
                                            {formatFirestoreTimestamp(
                                                fighter.birthdate
                                            )}
                                        </td>
                                        <td>{fighter.icOrPassport}</td>
                                        {/* <td>{formatFirestoreTimestamp(fighter.createdAt)}</td> */}
                                        {/* <td>{formatFirestoreTimestamp(fighter.updateAt)}</td> */}
                                        {/* Gym Name */}
                                        <td>
                                            <div className="border flex flex-row items-center justify-between gap-1 cursor-pointer">
                                                <p
                                                    className="pl-2 truncate w-48"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(
                                                            fighter.gymNameOrTeamName
                                                        );
                                                        toast(
                                                            "📋 Copied Gym/Team Name to Clipboard"
                                                        );
                                                    }}
                                                >
                                                    {fighter.gymNameOrTeamName}
                                                </p>
                                                <a
                                                    className="btn btn-ghost btn-sm h-full btn-square"
                                                    target="_blank"
                                                    href={`https://www.google.com/search?q=${fighter.gymNameOrTeamName}`}
                                                    rel="noreferrer"
                                                >
                                                    <FaGoogle className="h-4" />
                                                </a>
                                            </div>
                                        </td>
                                        {/* Music */}
                                        <td>
                                            <div className="border flex flex-row items-center justify-between gap-1 cursor-pointer">
                                                <p
                                                    className="pl-2 truncate w-48"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(
                                                            fighter.walkOutMusic
                                                        );
                                                        toast(
                                                            "📋 Copied Music to Clipboard"
                                                        );
                                                    }}
                                                >
                                                    {fighter.walkOutMusic}
                                                </p>
                                                <a
                                                    className="btn btn-ghost btn-sm h-full btn-square"
                                                    target="_blank"
                                                    href={`https://www.youtube.com/results?search_query=${fighter.walkOutMusic}`}
                                                    rel="noreferrer"
                                                >
                                                    <FaYoutube className="h-4" />
                                                </a>
                                            </div>
                                        </td>
                                        {/* Email */}
                                        <td>
                                            <div className="border flex flex-row items-center justify-between gap-1 cursor-pointer">
                                                <p
                                                    className="pl-2 truncate w-48"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(
                                                            fighter?.email?.toLowerCase()
                                                        );
                                                        toast(
                                                            "📋 Copied Email to Clipboard"
                                                        );
                                                    }}
                                                >
                                                    {fighter?.email?.toLowerCase()}
                                                </p>
                                                <a
                                                    className="btn btn-ghost btn-sm h-full btn-square"
                                                    href={`mailto:${fighter?.email?.toLowerCase()}`}
                                                >
                                                    <Mail className="h-4" />
                                                </a>
                                            </div>
                                        </td>
                                        {/* IG Username */}
                                        <td>
                                            <div className="border flex flex-row items-center justify-between gap-1 cursor-pointer">
                                                <p
                                                    className="pl-2 truncate w-48"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(
                                                            fighter.igUsername
                                                        );
                                                        toast(
                                                            "📋 Copied IG Username to Clipboard"
                                                        );
                                                    }}
                                                >
                                                    {fighter.igUsername}
                                                </p>
                                                <a
                                                    className="btn btn-ghost btn-sm h-full btn-square"
                                                    target="_blank"
                                                    href={`https://www.instagram.com/${fighter?.igUsername?.replace(
                                                        "@",
                                                        ""
                                                    )}`}
                                                    rel="noreferrer"
                                                >
                                                    <FaInstagram className="h-4" />
                                                </a>
                                            </div>
                                        </td>
                                        {/* Phone */}
                                        <td>
                                            <div className="border flex flex-row items-center justify-between gap-1 cursor-pointer">
                                                <p
                                                    className="pl-2 truncate w-48"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(
                                                            fighter.phoneNumber
                                                        );
                                                        toast(
                                                            "📋 Copied Phone No to Clipboard"
                                                        );
                                                    }}
                                                >
                                                    {fighter.phoneNumber}
                                                </p>
                                                <a
                                                    className="btn btn-ghost btn-sm h-full btn-square"
                                                    target="_blank"
                                                    href={`https://web.whatsapp.com/send/?phone=${fighter?.phoneNumber
                                                        ?.replaceAll("+", "")
                                                        ?.replaceAll("-", "")
                                                        ?.replaceAll("-", "")}`}
                                                    rel="noreferrer"
                                                >
                                                    <FaWhatsapp className="h-4" />
                                                </a>
                                            </div>
                                        </td>
                                        {/* Emergency Contract */}
                                        <td>
                                            <div className="border flex flex-row items-center justify-between gap-1 cursor-pointer">
                                                <p
                                                    className="pl-2 truncate w-48"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigator.clipboard.writeText(
                                                            fighter.emergencyContactNumber
                                                        );
                                                        toast(
                                                            "📋 Copied Emergency Contact No to Clipboard"
                                                        );
                                                    }}
                                                >
                                                    {
                                                        fighter.emergencyContactNumber
                                                    }
                                                </p>
                                                <a
                                                    className="btn btn-ghost btn-sm h-full btn-square"
                                                    target="_blank"
                                                    href={`tel:${fighter.emergencyContactNumber}`}
                                                    rel="noreferrer"
                                                >
                                                    <Phone className="h-4" />
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="flex-center w-full p-2">
                        {/* Display messages based on state */}
                        {loading && <div className="loading loading-bars" />}
                        {!loading && fighters.length === 0 && (
                            <p className="flex-center h-36">NO RESULT</p>
                        )}
                        {!loading && fighters.length > 0 && !hasMore && (
                            <p className="opacity-70">END</p>
                        )}
                    </div>
                    {/* Sentinel element for the Intersection Observer */}
                    <div ref={observerRef} style={{ height: "1px" }} />
                </div>
                <div className="divider divider-primary m-0" />
                {/* Bottom Search Bar */}
                <div className="w-full">
                    <div className="flex-center flex-row p-4 pt-2 gap-2">
                        <input
                            type="text"
                            className="input input-bordered w-full"
                            placeholder="Search by Name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {/* <button className='btn btn-primary' onClick={handleCreate}>Create</button> */}
                    </div>
                </div>
            </div>

            {!!selectedFighter ? (
                <>
                    <div className="absolute bg-base-100 md:relative w-full md:w-1/2 md:max-w-[36rem]  overflow-y-auto">
                        <ManageFighter
                            key={selectedFighter.id}
                            fighter={selectedFighter}
                            fighters={fighters}
                            handleClose={handleOnPanelClose}
                        />
                    </div>
                </>
            ) : null}
        </div>
    );
}
